/**
 * V3.1p-fix2 — Vue par Zone (carte de France des dossiers CEE/MPA).
 *
 * Affiche une carte interactive des départements français colorée selon
 * la densité de dossiers, avec tooltip au survol (dept, nb dossiers,
 * total primes, liste des AMO qui couvrent la zone).
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  MapPin, Users, FolderOpen, Loader2, Euro, ExternalLink, AlertTriangle,
} from 'lucide-react';

const GEO_URL = '/france-departements.geojson';
const AMO_ALERT_THRESHOLD = 3; // > 3 dossiers sans AMO = alerte "zone blanche"

// Palette simplifiée et contrastée (3 catégories bien distinctes)
const COLOR_NO_DATA = '#f1f5f9';         // slate-100 — aucun dossier, aucun AMO
const COLOR_COVERED = '#fde68a';         // amber-200 saturé — couvert AMO sans dossier
// V3.1p-fix5 — Nouvelle palette VERTE pour les zones « idéales » : AMO couvre + dossiers présents
const COLOR_RAMP_WITH_AMO = [
  '#bbf7d0', // 1 dossier avec AMO (green-200)
  '#4ade80', // 2-3 dossiers avec AMO (green-400)
  '#16a34a', // 4-9 dossiers avec AMO (green-600)
  '#14532d', // 10+ dossiers avec AMO (green-900)
];
const COLOR_RAMP = [                     // 4 paliers de bleu — dossiers SANS AMO (zone blanche)
  '#bfdbfe', // 1 dossier (blue-200)
  '#60a5fa', // 2-3 dossiers (blue-400)
  '#2563eb', // 4-9 dossiers (blue-600)
  '#1e3a8a', // 10+ dossiers (blue-900)
];
const COLOR_STROKE = '#94a3b8';          // slate-400 — contour toujours visible

// Paliers fixes de densité (quantile naturel, plus lisible que l'auto-scale)
const RAMP_THRESHOLDS = [1, 2, 4, 10];   // minima inclusifs
const COVERED_NO_DOSSIER = COLOR_COVERED;  // alias conservé pour la legend

// Palette (clair → saturé) pour la densité de dossiers — remplacée plus haut
// (ancien bloc supprimé lors du nettoyage V3.1p-fix4)

export default function CeeZonesView() {
  const nav = useNavigate();
  const [stats, setStats] = useState([]);
  const [totals, setTotals] = useState({ total_dossiers: 0, total_depts_with_dossiers: 0, total_depts_covered_by_amo: 0 });
  const [loading, setLoading] = useState(true);
  const [tooltip, setTooltip] = useState(null); // { x, y, content }
  const mapRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/cee/dossiers/_stats-by-zone');
        setStats(r.data?.items || []);
        setTotals({
          total_dossiers: r.data?.total_dossiers || 0,
          total_depts_with_dossiers: r.data?.total_depts_with_dossiers || 0,
          total_depts_covered_by_amo: r.data?.total_depts_covered_by_amo || 0,
        });
      } finally { setLoading(false); }
    })();
  }, []);

  const byDept = useMemo(() => {
    const m = {};
    stats.forEach((s) => { m[s.code] = s; });
    return m;
  }, [stats]);

  // Palette fixe par paliers naturels (plus lisible que scaleQuantize auto)
  // V3.1p-fix5 — VERT si AMO couvre la zone, BLEU sinon (= zone blanche)
  const getFillForDept = (deptCode) => {
    const s = byDept[deptCode];
    if (!s) return COLOR_NO_DATA;
    if ((s.count || 0) === 0) return COLOR_COVERED;
    const ramp = s.has_amo_coverage ? COLOR_RAMP_WITH_AMO : COLOR_RAMP;
    const c = s.count;
    if (c >= 10) return ramp[3];
    if (c >= 4) return ramp[2];
    if (c >= 2) return ramp[1];
    return ramp[0];
  };

  const handleMouseEnter = (deptCode, deptNom, evt) => {
    const s = byDept[deptCode];
    const content = {
      dept: `${deptCode} — ${deptNom}`,
      count: s?.count || 0,
      prime: s?.total_prime_eur || 0,
      amos: s?.amos || [],
      by_status: s?.by_status || {},
    };
    const rect = mapRef.current?.getBoundingClientRect();
    setTooltip({
      x: evt.clientX - (rect?.left || 0),
      y: evt.clientY - (rect?.top || 0),
      content,
    });
  };

  const handleMouseLeave = () => setTooltip(null);

  const handleClickDept = (deptCode) => {
    nav(`/cee/dossiers?cp=${deptCode}`);
  };

  // V3.1p-fix3 — Alertes : départements avec > N dossiers mais aucun AMO
  const alertsNoAmo = useMemo(
    () => stats
      .filter((s) => (s.count || 0) > AMO_ALERT_THRESHOLD && (s.amos || []).length === 0)
      .sort((a, b) => (b.count || 0) - (a.count || 0)),
    [stats],
  );

  const sortedList = useMemo(
    () => [...stats].sort((a, b) => (b.count || 0) - (a.count || 0)),
    [stats],
  );

  return (
    <div className="p-6 space-y-6" data-testid="cee-zones-view">
      {/* Header + KPIs */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <MapPin className="w-6 h-6 text-blue-600" />
          Couverture terrain — Vue par zone
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Densité de vos dossiers et couverture AMO sur l'ensemble du territoire français.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 border-slate-200">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Total dossiers</div>
              <div className="text-3xl font-bold text-slate-900 mt-1">{totals.total_dossiers}</div>
            </div>
            <FolderOpen className="w-8 h-8 text-blue-100" />
          </div>
        </Card>
        <Card className="p-4 border-slate-200">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Départements actifs</div>
              <div className="text-3xl font-bold text-slate-900 mt-1">
                {totals.total_depts_with_dossiers}
                <span className="text-base font-normal text-slate-400">/96</span>
              </div>
            </div>
            <MapPin className="w-8 h-8 text-blue-100" />
          </div>
        </Card>
        <Card className="p-4 border-slate-200">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Couvert par AMO</div>
              <div className="text-3xl font-bold text-slate-900 mt-1">
                {totals.total_depts_covered_by_amo}
                <span className="text-base font-normal text-slate-400">/96</span>
              </div>
            </div>
            <Users className="w-8 h-8 text-amber-100" />
          </div>
        </Card>
      </div>

      {/* V3.1p-fix3 — Dashboard d'alerte : zones blanches à couvrir */}
      {alertsNoAmo.length > 0 && (
        <Card className="p-4 border-l-4 border-l-red-500 bg-red-50/50 border-red-200"
               data-testid="cee-zones-alerts">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-red-100 p-2 flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-red-900">
                  Zones blanches à couvrir
                </h3>
                <span className="text-xs bg-red-100 text-red-800 px-2 py-0.5 rounded-full font-semibold">
                  {alertsNoAmo.length} département{alertsNoAmo.length > 1 ? 's' : ''}
                </span>
              </div>
              <p className="text-xs text-red-800/80 mt-0.5 mb-3">
                Ces départements ont plus de {AMO_ALERT_THRESHOLD} dossiers sans AMO attribué.
                Attribuez un AMO existant ou créez-en un nouveau couvrant ces zones.
              </p>
              <div className="flex flex-wrap gap-2">
                {alertsNoAmo.map((a) => (
                  <button
                    key={a.code}
                    onClick={() => handleClickDept(a.code)}
                    className="group inline-flex items-center gap-2 bg-white border border-red-200 hover:border-red-400 hover:bg-red-50 rounded-lg px-3 py-1.5 transition-colors"
                    data-testid={`cee-zones-alert-${a.code}`}
                  >
                    <span className="font-mono font-bold text-red-700">{a.code}</span>
                    <span className="text-slate-500">·</span>
                    <span className="text-sm text-slate-800">
                      <b className="text-red-700">{a.count}</b> dossier{a.count > 1 ? 's' : ''}
                    </span>
                    {a.total_prime_eur > 0 && (
                      <>
                        <span className="text-slate-300">·</span>
                        <span className="text-xs text-emerald-700 font-semibold">
                          {Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(a.total_prime_eur)}
                        </span>
                      </>
                    )}
                    <ExternalLink className="w-3 h-3 text-slate-400 group-hover:text-red-500 transition-colors" />
                  </button>
                ))}
              </div>
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <Link
                  to="/admin/amos"
                  className="text-xs text-red-700 hover:text-red-900 font-semibold underline underline-offset-2"
                >
                  → Créer un AMO pour couvrir ces zones
                </Link>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Map + Legend */}
      <Card className="p-4 border-slate-200 relative" ref={mapRef}>
        {loading && (
          <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
          </div>
        )}
        <div className="flex items-center gap-x-5 gap-y-2 flex-wrap mb-3 text-[11px] pb-3 border-b border-slate-100">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-4 h-4 rounded"
                   style={{ background: COLOR_NO_DATA, border: `1px solid ${COLOR_STROKE}` }}></span>
            <span className="text-slate-600">Aucun dossier · pas d'AMO</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-4 h-4 rounded"
                   style={{ background: COLOR_COVERED, border: '1px solid rgba(0,0,0,.1)' }}></span>
            <span className="text-slate-600">Couvert AMO · aucun dossier</span>
          </div>
          <div className="h-5 w-px bg-slate-200"></div>
          <div className="flex items-center gap-3">
            <span className="font-semibold text-emerald-700 uppercase tracking-wide text-[10px]">
              AMO + dossiers
            </span>
            {[
              { c: COLOR_RAMP_WITH_AMO[0], l: '1' },
              { c: COLOR_RAMP_WITH_AMO[1], l: '2-3' },
              { c: COLOR_RAMP_WITH_AMO[2], l: '4-9' },
              { c: COLOR_RAMP_WITH_AMO[3], l: '10+' },
            ].map((p) => (
              <div key={p.l} className="flex items-center gap-1">
                <span className="inline-block w-4 h-4 rounded"
                       style={{ background: p.c, border: '1px solid rgba(0,0,0,.08)' }}></span>
                <span className="text-slate-600 font-medium">{p.l}</span>
              </div>
            ))}
          </div>
          <div className="h-5 w-px bg-slate-200"></div>
          <div className="flex items-center gap-3">
            <span className="font-semibold text-blue-700 uppercase tracking-wide text-[10px]">
              Dossiers sans AMO
            </span>
            {[
              { c: COLOR_RAMP[0], l: '1' },
              { c: COLOR_RAMP[1], l: '2-3' },
              { c: COLOR_RAMP[2], l: '4-9' },
              { c: COLOR_RAMP[3], l: '10+' },
            ].map((p) => (
              <div key={p.l} className="flex items-center gap-1">
                <span className="inline-block w-4 h-4 rounded"
                       style={{ background: p.c, border: '1px solid rgba(0,0,0,.08)' }}></span>
                <span className="text-slate-600 font-medium">{p.l}</span>
              </div>
            ))}
          </div>
        </div>

        <ComposableMap
          data-testid="cee-zones-map"
          projection="geoConicConformal"
          projectionConfig={{ center: [2.5, 46.5], parallels: [44, 49], scale: 2800 }}
          width={800}
          height={700}
          style={{ width: '100%', height: 'auto', maxHeight: '70vh' }}
        >
          <Geographies geography={GEO_URL}>
            {({ geographies }) =>
              geographies.map((geo) => {
                const code = geo.properties.code;
                const nom = geo.properties.nom;
                const fill = getFillForDept(code);
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    fill={fill}
                    stroke={COLOR_STROKE}
                    strokeWidth={0.5}
                    onMouseEnter={(e) => handleMouseEnter(code, nom, e)}
                    onMouseMove={(e) => handleMouseEnter(code, nom, e)}
                    onMouseLeave={handleMouseLeave}
                    onClick={() => handleClickDept(code)}
                    style={{
                      default: { outline: 'none', transition: 'fill 150ms' },
                      hover: {
                        fill: '#f59e0b',
                        stroke: '#92400e',
                        strokeWidth: 1,
                        outline: 'none',
                        cursor: 'pointer',
                      },
                      pressed: { outline: 'none' },
                    }}
                    data-testid={`dept-${code}`}
                  />
                );
              })
            }
          </Geographies>
        </ComposableMap>

        {tooltip && (
          <div
            className="pointer-events-none absolute z-20 bg-white rounded-lg shadow-xl border border-slate-200 p-3 min-w-[220px] max-w-[280px]"
            style={{
              left: Math.min(tooltip.x + 12, (mapRef.current?.clientWidth || 0) - 290),
              top: tooltip.y + 12,
            }}
            data-testid="cee-zones-tooltip"
          >
            <div className="text-xs font-bold text-slate-800 border-b border-slate-100 pb-1 mb-2">
              {tooltip.content.dept}
            </div>
            <div className="space-y-1 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-slate-500">Dossiers</span>
                <span className="font-bold text-blue-700">{tooltip.content.count}</span>
              </div>
              {tooltip.content.prime > 0 && (
                <div className="flex items-center justify-between">
                  <span className="text-slate-500">Primes totales</span>
                  <span className="font-semibold text-emerald-700">
                    {Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(tooltip.content.prime)}
                  </span>
                </div>
              )}
              {Object.keys(tooltip.content.by_status).length > 0 && (
                <div className="pt-1 border-t border-slate-100 mt-1">
                  <div className="text-[10px] uppercase text-slate-400 mb-0.5">Par statut</div>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(tooltip.content.by_status).map(([k, v]) => (
                      <span key={k} className="inline-block bg-slate-100 text-slate-600 text-[10px] px-1.5 py-0.5 rounded">
                        {k}: {v}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="pt-1 border-t border-slate-100 mt-1">
                <div className="text-[10px] uppercase text-slate-400 mb-0.5">AMO couvrant</div>
                {tooltip.content.amos.length === 0 ? (
                  <span className="italic text-slate-400 text-[11px]">
                    Aucun AMO sur cette zone
                  </span>
                ) : (
                  <ul className="space-y-0.5">
                    {tooltip.content.amos.map((a) => (
                      <li key={a.amo_id} className="text-amber-800 font-medium text-[11px] flex items-center gap-1">
                        <Users className="w-2.5 h-2.5" /> {a.nom}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {tooltip.content.count > 0 && (
                <div className="pt-1.5 border-t border-slate-100 mt-1.5 text-center">
                  <span className="text-[10px] text-blue-600 font-semibold">
                    Cliquez pour voir les dossiers →
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </Card>

      {/* Top départements */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50">
          <h3 className="font-semibold text-slate-800 text-sm">
            Classement des départements
          </h3>
        </div>
        {loading ? (
          <div className="p-6 text-center">
            <Loader2 className="w-5 h-5 animate-spin mx-auto text-slate-400" />
          </div>
        ) : sortedList.length === 0 ? (
          <div className="p-6 text-center text-slate-500 text-sm">
            Aucun dossier géolocalisé pour le moment.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-100">
                <tr>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600 uppercase">Département</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-slate-600 uppercase">Dossiers</th>
                  <th className="text-right px-4 py-2 text-xs font-semibold text-slate-600 uppercase">Primes</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600 uppercase">Par statut</th>
                  <th className="text-left px-4 py-2 text-xs font-semibold text-slate-600 uppercase">AMO couvrant</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {sortedList.map((s) => (
                  <tr key={s.code} className="hover:bg-slate-50"
                      data-testid={`cee-zones-row-${s.code}`}>
                    <td className="px-4 py-2.5">
                      <span className="font-mono font-bold text-slate-800">{s.code}</span>
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {s.count > 0 ? (
                        <span className="font-bold text-blue-700">{s.count}</span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {s.total_prime_eur > 0 ? (
                        <span className="font-semibold text-emerald-700 text-xs">
                          {Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(s.total_prime_eur)}
                        </span>
                      ) : (
                        <span className="text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap gap-1">
                        {Object.entries(s.by_status || {}).map(([k, v]) => (
                          <span key={k} className="text-[10px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">
                            {k} · {v}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      {(s.amos || []).length === 0 ? (
                        <span className="italic text-slate-400 text-xs">
                          Aucun AMO
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {s.amos.map((a) => (
                            <Badge key={a.amo_id}
                                    className="bg-amber-50 text-amber-800 border border-amber-200 font-normal">
                              <Users className="w-3 h-3 mr-1" /> {a.nom}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      {s.count > 0 && (
                        <Link
                          to={`/cee/dossiers?cp=${s.code}`}
                          className="text-xs text-[#1C49A6] hover:underline inline-flex items-center gap-1"
                        >
                          Dossiers <ExternalLink className="w-3 h-3" />
                        </Link>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
